import { emptySplitApi } from 'store/emptyApi';

export interface PulseSurvey {
  id?: number;
  assessment_schedule_structure?: number;
  status: number;
  organization: number;
  organization_name?: string;
  team?: number | null;
  team_ids?: number[];
  team_name?: string | null;
  start_date_time: string;
  end_date_time: string;
  assessment_question_set: number;
  assessment_question_set_title?: string;
  reminder_enabled: boolean;
  reminder_schedule?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PulseSurveyStructure {
  id: number;
  name: string;
  organization: number;
  organization_name?: string;
  team?: number | null;
  team_ids?: number[];
  team_name?: string | null;
  assessment_question_set: number;
  assessment_question_set_title?: string;
  recurrence_type: number;
  recurrence_rule?: string | null;
  first_start_date_time: string;
  first_end_date_time: string;
  duration_days: number;
  reminder_enabled: boolean;
  reminder_schedule?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  schedules: PulseSurvey[];
}

export type PulseSurveyWritePayload = Partial<PulseSurvey> & {
  team_ids?: number[];
  recurrence_type?: number;
  recurrence_rule?: string | null;
  is_active?: boolean;
};

export type PulseSurveyStructureWritePayload = {
  name?: string;
  team_ids?: number[];
  assessment_question_set?: number;
  start_date_time?: string;
  end_date_time?: string;
  duration_days?: number;
  reminder_enabled?: boolean;
  reminder_schedule?: string | null;
  recurrence_type?: number;
  recurrence_rule?: string | null;
  is_active?: boolean;
};

export interface AssessmentQuestionSetOption {
  id: number;
  type: number;
  title: string;
  scope: number;
  team?: number | null;
  organization?: number | null;
  is_archived?: boolean;
  questions?: AssessmentQuestionSetQuestion[];
}

export interface AssessmentQuestionSetQuestion {
  id?: number;
  pillar?: number | null;
  question: string;
  type?: number;
  theme?: number | null;
  sort_order?: number;
  config?: Record<string, unknown>;
  allow_feedback?: boolean;
  required?: boolean;
}

export interface AssessmentQuestionSetWritePayload {
  type: number;
  title: string;
  scope: number;
  team?: number | null;
  is_archived?: boolean;
  questions?: AssessmentQuestionSetQuestion[];
}

export interface AssessmentQuestionSetPatchPayload {
  title?: string;
  team?: number | null;
  is_archived?: boolean;
  questions?: AssessmentQuestionSetQuestion[];
}

/** "canceled": uninvited after the email went out; their link no longer works. */
export type PulseInviteState = 'pending' | 'sent' | 'failed' | 'canceled' | 'completed';

export interface PulseParticipant {
  id: number;
  /** Null once the person was deleted; their row (and answers) stay. */
  employee_id: number | null;
  name: string;
  team_name: string | null;
  email: string | null;
  invitation_url?: string | null;
  email_history?: Array<{
    email: string;
    sent_date: string | null;
    status: string;
    error_message?: string | null;
  }>;
  invite_state: PulseInviteState;
  invite_sent_at: string | null;
  /** Only set when invite_state is "failed". */
  delivery_status: string | null;
}

/**
 * Aggregate participation totals. Pulse surveys are anonymous: the API never
 * says WHO completed, only how many. `completed` / `response_rate` are null
 * until at least `min_group_size` people are invited.
 */
export interface PulseParticipation {
  invited: number;
  emailed: number;
  completed: number | null;
  /** 0..1 */
  response_rate: number | null;
  finalized: boolean;
  min_group_size: number;
  finalized_on?: string;
}

export interface PulseParticipantsResponse {
  /**
   * Invite roster for this schedule.
   *
   * The backend now retains participant records after finalization and simply
   * prevents further submissions.
   */
  participants: PulseParticipant[];
  participation: PulseParticipation;
  can_finalize: boolean;
}

export type InvitePulseParticipantsRequest =
  | { employee_ids: number[] }
  | { audience: 'team' };

export interface InvitePulseParticipantsResponse {
  assessment_id: number | null;
  invited: number;
  retried: number;
  already_invited: number;
  not_eligible: number[];
  queued: boolean | number;
}

export interface FinalizePulseSurveyResponse {
  participation: PulseParticipation;
}

export interface PulseSurveyLikertScoreCount {
  score: number;
  count: number;
}

export interface PulseSurveyOptionCount {
  value: number;
  option: string;
  count: number;
}

export interface PulseSurveyLikertQuestionAnalytics {
  question_id: number;
  question: string;
  question_type?: 'likert' | 'multiple_choice' | 'multi_select';
  max_selections?: number | null;
  pillar?: number | null;
  pillar_label?: string | null;
  theme?: number | null;
  theme_label?: string | null;
  counts?: PulseSurveyLikertScoreCount[];
  option_counts?: PulseSurveyOptionCount[];
  total_responses: number;
  feedback_groups?: {
    score: number;
    label?: string;
    feedbacks: string[];
  }[];
}

export interface PulseSurveyAnalyticsParticipantTeam {
  id: number;
  name: string;
  member_count: number;
  response_count: number;
}

export interface PulseSurveyLikertAnalyticsResponse {
  schedule_id: number;
  assessment_id: number | null;
  organization_name: string;
  assessment_period_id: number | null;
  selected_participant_team_id: number | null;
  participant_teams: PulseSurveyAnalyticsParticipantTeam[];
  questions: PulseSurveyLikertQuestionAnalytics[];
}

const PULSE_SURVEYS_URL = '/api/portal/v1/assessments/pulse-surveys';

const PulseSurveyPortalTags = {
  PulseSurvey: 'PortalPulseSurvey',
  PulseParticipants: 'PortalPulseParticipants',
} as const;

export const pulseSurveyPortalApi = emptySplitApi
  .enhanceEndpoints({
    addTagTypes: [PulseSurveyPortalTags.PulseSurvey, PulseSurveyPortalTags.PulseParticipants],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listPulseSurveys: build.query<PulseSurvey[], { status?: number; team_id?: number; assessment_question_set?: number } | void>({
        query: (params) => ({
          url: '/api/portal/v1/assessments/pulse-surveys/',
          method: 'GET',
          params: params || undefined,
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map((row) => ({ type: PulseSurveyPortalTags.PulseSurvey, id: row.id })),
                { type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' },
              ]
            : [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      listPulseSurveyStructures: build.query<PulseSurveyStructure[], { team_id?: number; assessment_question_set?: number } | void>({
        query: (params) => ({
          url: '/api/portal/v1/assessments/pulse-surveys/structures/',
          method: 'GET',
          params: params || undefined,
        }),
        providesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      createPulseSurvey: build.mutation<PulseSurvey, { payload: PulseSurveyWritePayload }>({
        query: ({ payload }) => ({
          url: '/api/portal/v1/assessments/pulse-surveys/',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      updatePulseSurvey: build.mutation<PulseSurvey, { id: number; payload: PulseSurveyWritePayload }>({
        query: ({ id, payload }) => ({
          url: `/api/portal/v1/assessments/pulse-surveys/${id}/`,
          method: 'PATCH',
          body: payload,
        }),
        invalidatesTags: (_, __, arg) => [
          { type: PulseSurveyPortalTags.PulseSurvey, id: arg.id },
          { type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' },
        ],
      }),
      updatePulseSurveyStructure: build.mutation<PulseSurveyStructure, { id: number; payload: PulseSurveyStructureWritePayload }>({
        query: ({ id, payload }) => ({
          url: `/api/portal/v1/assessments/pulse-surveys/structures/${id}/`,
          method: 'PATCH',
          body: payload,
        }),
        invalidatesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      deletePulseSurveyStructure: build.mutation<void, { id: number }>({
        query: ({ id }) => ({
          url: `/api/portal/v1/assessments/pulse-surveys/structures/${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      deletePulseSurvey: build.mutation<void, { id: number }>({
        query: ({ id }) => ({
          url: `/api/portal/v1/assessments/pulse-surveys/${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, __, arg) => [
          { type: PulseSurveyPortalTags.PulseSurvey, id: arg.id },
          { type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' },
        ],
      }),
      getPulseSurvey: build.query<PulseSurvey, number>({
        query: (id) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/`,
          method: 'GET',
        }),
        providesTags: (_, __, id) => [{ type: PulseSurveyPortalTags.PulseSurvey, id }],
      }),
      getPulseParticipants: build.query<PulseParticipantsResponse, number>({
        query: (id) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/participants/`,
          method: 'GET',
        }),
        providesTags: (_, __, id) => [{ type: PulseSurveyPortalTags.PulseParticipants, id }],
      }),
      getPulseSurveyAnalytics: build.query<
        PulseSurveyLikertAnalyticsResponse,
        { id: number; participant_team_id?: number }
      >({
        query: ({ id, participant_team_id }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/analytics/`,
          method: 'GET',
          params: participant_team_id ? { participant_team_id } : undefined,
        }),
        providesTags: (_, __, arg) => [{ type: PulseSurveyPortalTags.PulseParticipants, id: arg.id }],
      }),
      invitePulseParticipants: build.mutation<
        InvitePulseParticipantsResponse,
        { id: number; body: InvitePulseParticipantsRequest }
      >({
        query: ({ id, body }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/participants/`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_, __, arg) => [{ type: PulseSurveyPortalTags.PulseParticipants, id: arg.id }],
      }),
      /**
       * Uninvite. An unsent invite is deleted (204, no body); a sent one is
       * canceled and stays listed (200, `{ invite_state: 'canceled' }`).
       */
      removePulseParticipant: build.mutation<{ invite_state: 'canceled' } | null, { id: number; participantId: number }>({
        query: ({ id, participantId }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/participants/${participantId}/`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, __, arg) => [{ type: PulseSurveyPortalTags.PulseParticipants, id: arg.id }],
      }),
      remindPulseParticipants: build.mutation<{ queued: boolean }, { id: number }>({
        query: ({ id }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/remind/`,
          method: 'POST',
        }),
      }),
      remindPulseParticipant: build.mutation<{ queued: boolean }, { id: number; participantId: number }>({
        query: ({ id, participantId }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/participants/${participantId}/remind/`,
          method: 'POST',
        }),
        invalidatesTags: (_, __, arg) => [{ type: PulseSurveyPortalTags.PulseParticipants, id: arg.id }],
      }),
      finalizePulseSurvey: build.mutation<FinalizePulseSurveyResponse, { id: number }>({
        query: ({ id }) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/finalize/`,
          method: 'POST',
        }),
        invalidatesTags: (_, __, arg) => [
          { type: PulseSurveyPortalTags.PulseParticipants, id: arg.id },
          { type: PulseSurveyPortalTags.PulseSurvey, id: arg.id },
          { type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' },
        ],
      }),
      /** A signed, shareable link to the survey's preview in the assessment app. */
      getPulseSurveyPreviewLink: build.query<{ url: string }, number>({
        query: (id) => ({
          url: `${PULSE_SURVEYS_URL}/${id}/preview-link/`,
          method: 'GET',
        }),
        // Always sign a fresh link (they expire).
        keepUnusedDataFor: 0,
      }),
      listPulseQuestionSets: build.query<AssessmentQuestionSetOption[], void>({
        query: () => ({
          url: '/api/portal/v1/assessments/question-sets/',
          method: 'GET',
          params: {
            type: 1,
            include_archived: false,
          },
        }),
        providesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      createPulseQuestionSet: build.mutation<AssessmentQuestionSetOption, { payload: AssessmentQuestionSetWritePayload }>({
        query: ({ payload }) => ({
          url: '/api/portal/v1/assessments/question-sets/',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
      patchPulseQuestionSet: build.mutation<AssessmentQuestionSetOption, { id: number; payload: AssessmentQuestionSetPatchPayload }>({
        query: ({ id, payload }) => ({
          url: `/api/portal/v1/assessments/question-sets/${id}/`,
          method: 'PATCH',
          body: payload,
        }),
        invalidatesTags: [{ type: PulseSurveyPortalTags.PulseSurvey, id: 'LIST' }],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useListPulseSurveysQuery,
  useListPulseSurveyStructuresQuery,
  useCreatePulseSurveyMutation,
  useUpdatePulseSurveyMutation,
  useUpdatePulseSurveyStructureMutation,
  useDeletePulseSurveyStructureMutation,
  useDeletePulseSurveyMutation,
  useListPulseQuestionSetsQuery,
  useGetPulseSurveyQuery,
  useGetPulseParticipantsQuery,
  useGetPulseSurveyAnalyticsQuery,
  useInvitePulseParticipantsMutation,
  useRemovePulseParticipantMutation,
  useRemindPulseParticipantsMutation,
  useRemindPulseParticipantMutation,
  useFinalizePulseSurveyMutation,
  useLazyGetPulseSurveyPreviewLinkQuery,
  useCreatePulseQuestionSetMutation,
  usePatchPulseQuestionSetMutation,
} = pulseSurveyPortalApi;
